import { PaymentRequest, PaymentMethod } from '../models/PaymentRequest';

// Get the correct API base URL based on environment
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const API_BASE_URL = getApiBaseUrl();

export const fetchPaymentRequests = async (status?: string): Promise<PaymentRequest[]> => {
  try {
    const token = localStorage.getItem('accessToken');
    const url = status ? `${API_BASE_URL}/payment-requests?status=${status}` : `${API_BASE_URL}/payment-requests`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch payment requests');
    }

    const data = await response.json();
    return data.paymentRequests;
  } catch (error) {
    throw error;
  }
};

export const fetchPaymentRequest = async (id: string): Promise<PaymentRequest> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payment-requests/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch payment request');
    }

    const data = await response.json();
    return data.paymentRequest;
  } catch (error) {
    throw error;
  }
};

export const createPaymentRequest = async (paymentRequest: Partial<PaymentRequest>): Promise<PaymentRequest> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payment-requests`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(paymentRequest)
    });

    if (!response.ok) {
      throw new Error('Failed to create payment request');
    }

    const data = await response.json();
    return data.paymentRequest;
  } catch (error) {
    throw error;
  }
};

export const processPayment = async (
  paymentRequestId: string, 
  paymentMethodId: string
): Promise<{ success: boolean; transactionId: string }> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payment-requests/${paymentRequestId}/pay`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ paymentMethodId })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to process payment');
    }

    const data = await response.json();
    return {
      success: true,
      transactionId: data.transactionId
    };
  } catch (error) {
    throw error;
  }
};

export const createStripePaymentSession = async (
  paymentRequestId: string,
  amount: number,
  currency: string
): Promise<{ sessionId: string; url: string }> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payments/stripe/create-session`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        paymentRequestId,
        amount,
        currency
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create Stripe payment session');
    }

    const data = await response.json();
    return {
      sessionId: data.sessionId,
      url: data.url
    };
  } catch (error) {
    throw error;
  }
};

export const createPayPalOrder = async (
  paymentRequestId: string,
  amount: number,
  currency: string
): Promise<{ orderId: string; approvalUrl: string }> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payments/paypal/create-order`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        paymentRequestId,
        amount,
        currency
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create PayPal order');
    }

    const data = await response.json();
    return {
      orderId: data.orderId,
      approvalUrl: data.approvalUrl
    };
  } catch (error) {
    throw error;
  }
};

export const initializePaystackPayment = async (
  paymentRequestId: string,
  amount: number,
  currency: string,
  email: string
): Promise<{ reference: string; authorizationUrl: string }> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payments/paystack/initialize`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        paymentRequestId,
        amount,
        currency,
        email
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to initialize Paystack payment');
    }

    const data = await response.json();
    return {
      reference: data.reference,
      authorizationUrl: data.authorizationUrl
    };
  } catch (error) {
    throw error;
  }
};

export const verifyPayment = async (
  paymentRequestId: string,
  provider: string,
  referenceId: string
): Promise<{ success: boolean; transactionId: string }> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payments/${provider}/verify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        paymentRequestId,
        referenceId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to verify payment');
    }

    const data = await response.json();
    return {
      success: data.success,
      transactionId: data.transactionId
    };
  } catch (error) {
    throw error;
  }
};

export const fetchPaymentMethods = async (): Promise<PaymentMethod[]> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payment-methods`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch payment methods');
    }

    const data = await response.json();
    return data.paymentMethods;
  } catch (error) {
    throw error;
  }
};

export const addPaymentMethod = async (paymentMethod: Partial<PaymentMethod>): Promise<PaymentMethod> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payment-methods`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(paymentMethod)
    });

    if (!response.ok) {
      throw new Error('Failed to add payment method');
    }

    const data = await response.json();
    return data.paymentMethod;
  } catch (error) {
    throw error;
  }
};

export const fetchPaymentHistory = async (): Promise<any[]> => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_BASE_URL}/payments/history`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch payment history');
    }

    return data.payments || [];
  } catch (error) {
    throw error;
  }
};

export const fetchAdminPaymentHistory = async (): Promise<any[]> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payments/admin`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch admin payment history');
    }

    const data = await response.json();
    return data.payments || [];
  } catch (error) {
    throw error;
  }
};

export const cancelPaymentRequest = async (id: string): Promise<void> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payment-requests/${id}/cancel`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to cancel payment request');
    }
  } catch (error) {
    throw error;
  }
};

export const markPaymentRequestAsPaid = async (id: string, transactionId?: string): Promise<void> => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/payment-requests/${id}/mark-paid`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ transactionId })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to mark payment request as paid');
    }
  } catch (error) {
    throw error;
  }
};

// Re-export the types
export type { PaymentRequest, PaymentMethod };